import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { login, loginUser } from "@/lib/api";
import { loginOwner } from "@/lib/api"; // NUEVO
import { toast } from "@/hooks/use-toast";
import { Loader2, Store, User, Building } from "lucide-react";
import {
  setCurrentShop,
  setCurrentUser,
  setCurrentOwner,
  setOwnerShops,
  setActiveShopId,
  getCurrentUser,
  getCurrentShop,
} from "@/lib/auth"; // NUEVAS
import type { Shop } from "@shared/schema";
import { resendVerificationEmail } from "@/lib/api";
import { indexedDBService } from "@/lib/db/indexed-db";
import { checkOwnerSuspension } from "@/lib/auth"; // ← AGREGAR ESTA LÍNEA
import { AuthService } from "@/lib/services/auth-offline";
import { syncManager } from "@/lib/services/sync-manager";

export default function UserLogin() {
  const [, setLocation] = useLocation();

  const [activeTab, setActiveTab] = useState<"employee" | "owner">("employee");
  const [employeeForm, setEmployeeForm] = useState({
    username: "",
    password: "",
  });
  const [ownerForm, setOwnerForm] = useState({ email: "", password: "" });

  // Shop login mutation (existing) - VERSIÓN MODIFICADA
  const shopLoginMutation = useMutation({
    mutationFn: (data: { username: string; password: string }) =>
      login(data.username, data.password),
    onSuccess: async (data: any) => {
      // data puede ser Shop o {shop, owner, otherShops}
      const shop = data.shop || data;
      const owner = data.owner;
      const token = data.token; // ← EXTRAER TOKEN SI EXISTE

      setCurrentShop(shop);

      // Usuario temporal para dueño de tienda
      const tempUser = {
        id: `shop_${shop.id}`,
        shopId: shop.id,
        username: shop.username,
        fullName: shop.owner,
        role: "supervisor" as const,
      };
      setCurrentUser(tempUser);

      // NUEVO: Guardar sesión en IndexedDB para habilitar login offline
      if (token && shop.username) {
        try {
          console.log(
            "🔐 [AUTH] Guardando sesión para offline (shop login)...",
          );
          // {Usar la contraseña REAL del formulario para generar el hash correcto}
          // {Nota: shop.username viene del backend, el password viene del formulario}
          const passwordForHash = employeeForm.password || shop.password || "";
          await AuthService.onlineLogin(shop.username, passwordForHash, token, `virtual_owner_${shop.id}`);
          console.log(
            `✅ [AUTH] Sesión offline habilitada para: ${shop.username}`,
          );
        } catch (authError: any) {
          console.warn(
            "⚠️ [AUTH] No se pudo guardar sesión offline:",
            authError.message,
          );
          // No bloqueamos el login si falla el guardado offline
        }
      }

      // NUEVO: Guardar owner si existe
      if (owner) {
        localStorage.setItem("shopOwner", JSON.stringify(owner));
      }

      // {VERIFICAR SI EL OWNER ESTÁ SUSPENDIDO}
      const isSuspended = await checkOwnerSuspension();

      if (isSuspended) {
        setLocation("/shop/suspended");
        return;
      }

      // {NUEVO: Sincronizar operaciones pendientes al hacer login online}
      if (token) {
        try {
          console.log("🔄 [ShopLogin] Verificando operaciones pendientes...");
          const result = await syncManager.checkAndSync();
          if (result && result.syncedCount > 0) {
            console.log(
              `✅ [ShopLogin] ${result.syncedCount} operaciones sincronizadas`,
            );
          }
        } catch (syncError) {
          console.warn("⚠️ [ShopLogin] No se pudo sincronizar:", syncError);
        }
      }

      toast({
        title: "Bienvenido",
        description: `Has iniciado sesión como ${shop.name}`,
      });
      setLocation("/shop/dashboard");
    },
    // COMENTARIO:
    // - Extraemos token de data.token si el backend lo devuelve.
    // - AuthService.onlineLogin() guarda la sesión en IndexedDB para login offline futuro.
    // - Usamos password vacío ("") como solución temporal; en producción se ajustará para recibir hash del backend.
    onError: (error: Error) => {
      console.error("❌ [ShopLogin] Error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // {NUEVO: Mutación para login offline - no contacta al backend}
  const offlineLoginMutation = useMutation({
    mutationFn: async (data: { username: string; password: string }) => {
      console.log(
        "📴 [OfflineLogin] Intentando login offline para:",
        data.username,
      );
      const token = await AuthService.offlineLogin(
        data.username,
        data.password,
      );
      return { token, username: data.username };
    },
    onSuccess: async (data: { token: string; username: string }) => {
      console.log("✅ [OfflineLogin] Login offline exitoso:", data.username);

      // {Estrategia: Primero intentar localStorage, luego IndexedDB, luego datos básicos}
      let savedUser = getCurrentUser();
      let savedShop = getCurrentShop();

      // {Si no hay datos en localStorage, intentar reconstruir desde IndexedDB}
      if (!savedUser || !savedShop) {
        console.log(
          "⚠️ [OfflineLogin] No hay datos en localStorage, buscando en IndexedDB...",
        );

        try {
          const session = await indexedDBService.getActiveSession();

          if (session) {
            console.log(
              "✅ [OfflineLogin] Sesión encontrada en IndexedDB:",
              session,
            );

            // {Reconstruir datos mínimos para navegar}
            savedUser = {
              id: session.userId || (session.shopId ? `virtual_owner_${session.shopId}` : "offline_user"),
              shopId: session.shopId,
              username: session.username,
              fullName: session.username,
              role: "supervisor",
            };

            // {Crear objeto shop mínimo - el dashboard usa pocos campos}
            savedShop = {
              id: session.shopId,
              name: "Modo Offline",
              owner: session.username,
              username: session.username,
              // {Campos requeridos por el tipo Shop que no usaremos offline}
              password: "",
              address: "",
              ownerId: "",
              createdAt: new Date().toISOString(),
              telegramChatId: null,
              telegramBotToken: null,
              telegramEnabled: false,
              lowStockAlerts: false,
              importantSaleAlerts: false,
              dailySummaryAlerts: false,
              newCustomerAlerts: false,
              importantSaleThreshold: "500.00",
              createdByAdmin: false,
              dailySummaryTime: "20:00",
            } as any;

            // {Guardar en localStorage para que el resto de la app funcione}
            setCurrentUser(savedUser);
            setCurrentShop(savedShop as any);
          }
        } catch (e) {
          console.warn(
            "⚠️ [OfflineLogin] No se pudo obtener sesión de IndexedDB:",
            e,
          );
        }
      }

      if (!savedUser || !savedShop) {
        toast({
          title: "Error",
          description:
            "No hay datos locales. Conéctate a internet primero para guardar tu sesión.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Modo Offline Activado",
        description: `Bienvenido ${savedUser.fullName} - Trabajando sin conexión`,
      });
      setLocation("/shop/dashboard");
    },
    onError: (error: Error) => {
      console.error("❌ [OfflineLogin] Error:", error.message);
      toast({
        title: "Error de autenticación",
        description:
          error.message === "NO_HAY_SESION_OFFLINE_PARA_ESTA_TIENDA"
            ? "No hay sesión guardada. Conéctate a internet primero."
            : error.message === "CREDENCIALES_INVALIDAS"
              ? "Usuario o contraseña incorrectos"
              : error.message === "SESION_OFFLINE_EXPIRADA_REQUIERE_CONEXION"
                ? "Sesión expirada. Conéctate a internet para renovar."
                : error.message,
        variant: "destructive",
      });
    },
  });

  // Mutación para login offline de owner
  const offlineOwnerLoginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const { AuthService } = await import("@/lib/services/auth-offline");
      const token = await AuthService.ownerOfflineLogin(data.email, data.password);
      return { token, email: data.email };
    },
    onSuccess: async (data: { token: string; email: string }) => {
      console.log("✅ [OfflineOwnerLogin] Login offline exitoso:", data.email);

      // Intentar reconstruir owner desde IndexedDB
      let savedOwner: any = null;
      try {
        const session = await indexedDBService.getActiveOwnerSession();
        if (session) {
          savedOwner = {
            id: session.ownerId,
            email: session.email,
            ownerName: session.email,
            businessName: "Modo Offline",
            planType: session.planType || "basic",
            currency: session.currency || "CUP",
          };
          // Guardar en localStorage para compatibilidad
          localStorage.setItem("currentOwner", JSON.stringify(savedOwner));
          localStorage.setItem("shopOwner", JSON.stringify(savedOwner));
          setCurrentOwner(savedOwner);
        }
      } catch (e) {
        console.warn("⚠️ [OfflineOwnerLogin] No se pudo reconstruir owner desde IndexedDB:", e);
      }

      if (!savedOwner) {
        toast({
          title: "Error",
          description: "No hay datos locales del owner. Conéctate a internet primero.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Modo Offline Activado",
        description: `Bienvenido ${savedOwner.email} - Trabajando sin conexión`,
      });
      setLocation("/owner/dashboard");
    },
    onError: (error: Error) => {
      console.error("❌ [OfflineOwnerLogin] Error:", error.message);
      toast({
        title: "Error de autenticación",
        description:
          error.message === "NO_HAY_SESION_OFFLINE_PARA_ESTE_OWNER"
            ? "No hay sesión guardada. Conéctate a internet primero."
            : error.message === "CREDENCIALES_INVALIDAS"
              ? "Email o contraseña incorrectos"
              : error.message === "SESION_OFFLINE_EXPIRADA_REQUIERE_CONEXION"
                ? "Sesión expirada. Conéctate a internet para renovar."
                : error.message,
        variant: "destructive",
      });
    },
  });

  // Mutación para empleados (usuarios de tienda) - VERSIÓN MODIFICADA
  const employeeLoginMutation = useMutation({
    mutationFn: (data: { username: string; password: string }) =>
      loginUser(data.username, data.password),
    onSuccess: async (data: {
      user: any;
      shop: Shop;
      owner?: any;
      token?: string;
    }) => {
      // {Validar que data tiene los campos requeridos}
      if (!data || !data.user || !data.shop) {
        console.error(
          "❌ [EmployeeLogin] Respuesta inválida del backend:",
          data,
        );
        toast({
          title: "Error",
          description: "Respuesta inválida del servidor. Intente de nuevo.",
          variant: "destructive",
        });
        return;
      }

      // {Primero guardamos los datos en localStorage}
      setCurrentUser(data.user);
      setCurrentShop(data.shop);
      if (data.owner) {
        localStorage.setItem("shopOwner", JSON.stringify(data.owner));
      }

      // NUEVO: Guardar sesión en IndexedDB para habilitar login offline
      if (data.token) {
        try {
          console.log("🔐 [AUTH] Guardando sesión para offline...");
          const passwordForHash = employeeForm.password || "";
          await AuthService.onlineLogin(
            data.user.username,
            passwordForHash,
            data.token,
            data.user.id,
          );
          console.log(
            `✅ [AUTH] Sesión offline habilitada para: ${data.user.username}`,
          );
        } catch (authError: any) {
          console.warn(
            "⚠️ [AUTH] No se pudo guardar sesión offline:",
            authError.message,
          );
        }
      } else {
        console.warn(
          "⚠️ [AUTH] Backend no devolvió token, login offline no disponible",
        );
      }

      // {VERIFICAR SI EL OWNER ESTÁ SUSPENDIDO}
      const isSuspended = await checkOwnerSuspension();

      if (isSuspended) {
        setLocation("/shop/suspended");
        return;
      }

      // {NUEVO: Sincronizar operaciones pendientes al hacer login online}
      if (data.token) {
        try {
          console.log("🔄 [Login] Verificando operaciones pendientes...");
          const result = await syncManager.checkAndSync();
          if (result && result.syncedCount > 0) {
            console.log(
              `✅ [Login] ${result.syncedCount} operaciones sincronizadas`,
            );
          }
        } catch (syncError) {
          console.warn("⚠️ [Login] No se pudo sincronizar:", syncError);
        }
      }

      toast({
        title: "Bienvenido",
        description: `Hola ${data.user.fullName} (${data.user.role})`,
      });
      if (data.user.role === "cashier") {
        setLocation("/shop/pos");
      } else if (data.user.role === "supervisor") {
        setLocation("/supervisor/dashboard");
      } else {
        setLocation("/shop/dashboard");
      }
    },
    // COMENTARIO:
    // - AuthService.onlineLogin() guarda token + hash en IndexedDB para futuros logins offline.
    // - Usamos password vacío ("") porque el hash se genera con la contraseña real que el usuario ingresó en el formulario, pero como no la tenemos aquí, el hash se generará con el valor que el usuario ingrese en login offline.
    // - Si el backend no devuelve token, solo logueamos una advertencia y continuamos con el login normal.
    onError: (error: Error) => {
      console.error("❌ [EmployeeLogin] Error:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutación para dueños (unificada)
  // Owner login mutation
  const ownerLoginMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      loginOwner(data.email, data.password),
    onSuccess: async (data: { owner: any; shops: any[] }) => {
      if (!data.owner) {
        console.error("❌ [OwnerLogin] Respuesta inválida del servidor (sin owner):", data);
        toast({
          title: "Error",
          description: "Respuesta inválida del servidor. Intenta de nuevo.",
          variant: "destructive",
        });
        return;
      }

      console.log("✅ Owner login exitoso:", data);

      // Guardar datos del owner
      setCurrentOwner(data.owner);
      setOwnerShops(data.shops);

      // Si hay tiendas, establecer la primera como activa
      if (data.shops.length > 0) {
        setActiveShopId(data.shops[0].id);
        setCurrentShop(data.shops[0]);

        // Crear usuario temporal para el shop (necesario para algunas funcionalidades)
        const tempUser = {
          id: `shop_${data.shops[0].id}`,
          shopId: data.shops[0].id,
          username: data.shops[0].username,
          fullName: data.shops[0].owner,
          role: "supervisor" as const,
        };
        setCurrentUser(tempUser);
      }

      // Guardar también en shopOwner para consistencia
      localStorage.setItem("currentOwner", JSON.stringify(data.owner));
      localStorage.setItem("shopOwner", JSON.stringify(data.owner));

      // Guardar sesión offline para owner
      if ((data as any).token && ownerForm.email) {
        try {
          const { AuthService } = await import("@/lib/services/auth-offline");
          await AuthService.ownerOnlineLogin(ownerForm.email, ownerForm.password, (data as any).token, {
            planType: data.owner?.planType,
            currency: data.owner?.currency,
          });
          console.log("✅ [AUTH] Sesión offline habilitada para owner:", ownerForm.email);
        } catch (e: any) {
          console.warn("⚠️ [AUTH] No se pudo guardar sesión offline de owner:", e.message);
        }
      }

      toast({
        title: "Bienvenido",
        description: `Hola ${data.owner.ownerName} (${data.owner.businessName})`,
      });

      // Redirigir según el plan
      if (data.owner.planType === "basic") {
        console.log("🔄 Redirigiendo a /owner/dashboard (plan básico)");
        setLocation("/owner/dashboard");
      } else {
        console.log(
          "🔄 Redirigiendo a /owner/dashboard (plan profesional/enterprise)",
        );
        setLocation("/owner/dashboard");
      }
    },
    onError: (error: any) => {
      console.error("❌ Error en owner login:", error);

      // ============ MANEJAR ERROR ESPECÍFICO DE EMAIL NO VERIFICADO ============
      if (
        error.message?.includes("EMAIL_NOT_VERIFIED") ||
        error.message?.includes("verifica tu email")
      ) {
        // Mostrar toast con opción de reenviar
        toast({
          title: "Email no verificado",
          description:
            "Por favor, verifica tu email antes de iniciar sesión. Revisa tu bandeja de entrada.",
          variant: "destructive",
          duration: 10000,
          action: (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await resendVerificationEmail(ownerForm.email);
                  toast({
                    title: "✅ Email reenviado",
                    description:
                      "Hemos enviado un nuevo enlace de verificación a tu email.",
                  });
                } catch (err: any) {
                  toast({
                    title: "Error",
                    description: err.message,
                    variant: "destructive",
                  });
                }
              }}
              className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
            >
              Reenviar email
            </Button>
          ),
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Error al iniciar sesión",
          variant: "destructive",
        });
      }
    },
  });

  const handleEmployeeLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log("🔍 [DEBUG] Iniciando login híbrido...");

    // {Estrategia: Intentar online, si falla o respuesta inválida → offline automático}
    if (navigator.onLine) {
      console.log("🌐 [DEBUG] Hay conexión, intentando login online...");

      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);

        const response = await fetch("/api/users/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(employeeForm),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (response.ok) {
          const data = await response.json();

          // {Validar que la respuesta TIENE los campos reales del backend}
          if (data.user && data.shop) {
            console.log(
              "✅ [DEBUG] Backend responde con datos reales, usando flujo online",
            );
            employeeLoginMutation.mutate(employeeForm);
            return;
          } else {
            // {El SW respondió con {status: 'queued_offline'} u otra respuesta falsa}
            console.warn(
              "⚠️ [DEBUG] Respuesta no válida del backend (probablemente SW):",
              data,
            );
          }
        }
      } catch (fetchError: any) {
        console.warn(
          "⚠️ [DEBUG] No se pudo contactar backend:",
          fetchError?.message || fetchError,
        );
      }
    }

    // {Si llegamos aquí: sin conexión real o SW respondió con datos falsos → intentar offline}
    console.log("📴 [DEBUG] Cambiando a login offline...");
    offlineLoginMutation.mutate(employeeForm);
  };

  const handleOwnerLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Intentar online primero si hay conexión
    if (navigator.onLine) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 3000);
        const response = await fetch("/api/auth/owner/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ownerForm),
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (response.ok) {
          const responseData = await response.json();
          // Validar que la respuesta TIENE datos reales del owner (no respuesta falsa del SW)
          if (responseData.owner && responseData.shops) {
            ownerLoginMutation.mutate(ownerForm);
            return;
          } else {
            console.warn("⚠️ [OwnerLogin] Respuesta no válida (probablemente SW):", responseData);
          }
        }
      } catch {
        // Fallback offline
      }
    }

    offlineOwnerLoginMutation.mutate(ownerForm);
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
      {/* {Indicador de modo offline} */}
      {!navigator.onLine && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black text-center py-1 text-sm font-medium z-50">
          📴 Modo sin conexión - Iniciar sesión con credenciales guardadas
        </div>
      )}
      <Card className="w-full max-w-md shadow-xl border-primary/10">
        <CardHeader className="text-center border-b bg-muted/10 pb-8">
          <CardTitle className="text-2xl font-heading font-bold uppercase tracking-wider">
            Punto de Venta
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Sistema de gestión multi-usuario
          </p>
        </CardHeader>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="employee" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Empleado
              </TabsTrigger>
              <TabsTrigger value="owner" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Dueño / Empresa
              </TabsTrigger>
            </TabsList>

            {/* Login para Empleados */}
            <TabsContent value="employee">
              <form onSubmit={handleEmployeeLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="employee-username">Usuario</Label>
                  <Input
                    id="employee-username"
                    type="text"
                    placeholder="usuario123"
                    value={employeeForm.username}
                    onChange={(e) =>
                      setEmployeeForm({
                        ...employeeForm,
                        username: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee-password">Contraseña</Label>
                  <Input
                    id="employee-password"
                    type="password"
                    placeholder="••••••••"
                    value={employeeForm.password}
                    onChange={(e) =>
                      setEmployeeForm({
                        ...employeeForm,
                        password: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={employeeLoginMutation.isPending}
                >
                  {employeeLoginMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    "Ingresar como Empleado"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Acceso para vendedores, cajeros y managers
                </p>
              </form>
            </TabsContent>

            {/* Login para Dueños (cualquier plan) */}
            <TabsContent value="owner">
              <form onSubmit={handleOwnerLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="owner-email">Correo Electrónico</Label>
                  <Input
                    id="owner-email"
                    type="email"
                    placeholder="empresa@ejemplo.com"
                    value={ownerForm.email}
                    onChange={(e) =>
                      setOwnerForm({ ...ownerForm, email: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-password">Contraseña</Label>
                  <Input
                    id="owner-password"
                    type="password"
                    placeholder="••••••••"
                    value={ownerForm.password}
                    onChange={(e) =>
                      setOwnerForm({ ...ownerForm, password: e.target.value })
                    }
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={ownerLoginMutation.isPending}
                >
                  {ownerLoginMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Ingresando...
                    </>
                  ) : (
                    "Ingresar como Dueño"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Acceso para propietarios (Plan Básico, Profesional o
                  Enterprise)
                </p>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    💡 Si eres dueño de una o varias tiendas, usa tu correo
                    electrónico. El sistema te redirigirá al dashboard
                    correspondiente según tu plan.
                  </p>
                </div>
              </form>
            </TabsContent>
            <div className="text-center pt-4 border-t mt-6">
              <p className="text-sm text-muted-foreground">
                ¿Eres dueño de negocio y no tienes cuenta?{" "}
                <Link
                  href="/register"
                  className="text-primary hover:underline font-medium"
                >
                  Regístrate aquí
                </Link>
              </p>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
